import React, { createContext, useState, useContext, useEffect } from 'react';
import magic from '../lib/magic';

const AuthContext = createContext();

const API_URL = '/api';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(0);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const isLoggedIn = await magic.user.isLoggedIn();
        if (isLoggedIn) {
          try {
            // Get fresh token
            await magic.user.getIdToken({ lifespan: 900 });
            const userData = await magic.user.getMetadata();
            setUser(userData);
            fetchUserCredits(userData.email);
          } catch (error) {
            console.error('Token refresh error:', error);
            // If token refresh fails, log out
            await logout();
          }
        }
      } catch (error) {
        console.error('Auth check error:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const loginWithCode = async (email) => {
    try {
      const didToken = await magic.auth.loginWithEmailOTP({ email });
      const userData = await magic.user.getMetadata();
      setUser(userData);

      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${didToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        throw new Error('Failed to register user');
      }

      await fetchUserCredits(email);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const fetchUserCredits = async (email) => {
    try {
      const didToken = await magic.user.getIdToken();
      console.log('Fetching credits for:', email);
      const response = await fetch(`/api/user/credits`, {
        headers: {
          'Authorization': `Bearer ${didToken}`
        }
      });
      
      if (!response.ok) {
        console.error('Credits fetch failed:', response.status, response.statusText);
        const errorData = await response.json().catch(() => ({}));
        console.error('Error data:', errorData);
        throw new Error(errorData.error || 'Failed to fetch credits');
      }
      
      const data = await response.json();
      console.log('Credits data received:', data);
      setCredits(data.credits);
      setPlan(data.plan);
      return data;
    } catch (error) {
      console.error('Error fetching credits:', error);
      // Don't throw the error, just return default values
      setCredits(0);
      setPlan({ name: 'Free', credits: 0, maxCredits: 0 });
      return { credits: 0, plan: { name: 'Free', credits: 0, maxCredits: 0 } };
    }
  };

  const spendCredit = async () => {
    try {
      const didToken = await magic.user.getIdToken();
      const response = await fetch(`/api/user/credits/use`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${didToken}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to use credit');
      }
      
      const data = await response.json();
      setCredits(data.credits);
      return true;
    } catch (error) {
      console.error('Error using credit:', error);
      return false;
    }
  };

  const logout = async () => {
    try {
      await magic.user.logout();
      setUser(null);
      setCredits(0);
      setPlan(null);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const value = {
    user,
    loginWithCode,
    logout,
    loading,
    credits,
    plan,
    useCredit: spendCredit,
    fetchUserCredits,
    setCredits
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
} 